// Pure, DOM-free helpers (exported for unit testing — see detailedTablePlugin.spec.js).
//
// BBSW-9807 fix: totals must be computed PER COLUMN, never merged across an entire
// label-group. In `mode:'paired'` (BarChart.vue), enabling "Comparer avec une période"
// produces a base-period dataset and a compare-period dataset that share the SAME
// `label` (only their `stack` differs, so the chart can draw them as adjacent bars).
// Grouping datasets by `label` therefore puts base+compare in the same group — summing
// every column's values into one number silently adds base-period-total +
// compare-period-total together, which is meaningless. Keeping the sum per-column (one
// total per Base/Compare — or per series in `mode:'series'`) avoids that merge.
export const computeColumnTotals = (columns) => {
  return (columns ?? []).map((col) => {
    const colDetails = (col.details ?? []).flat().filter(Boolean)
    let hasNumericValue = false
    const total = colDetails.reduce((sum, detail) => {
      if (typeof detail?.value === 'number') {
        hasNumericValue = true
        return sum + detail.value
      }
      return sum
    }, 0)
    return { title: col.title, total, hasNumericValue }
  })
}

// Locale-aware number formatting (thousands grouping + at most 2 decimals), so an
// accumulated floating point total (e.g. 5892371.360000008) renders as "5 892 371,36"
// instead of the raw float. Mirrors the locale-resolution strategy already used by
// `helper.js`'s `formatCurrency` (html lang attribute, fallback 'fr-BE').
export const formatGroupNumber = (value, locale = null) => {
  const htmlLang = typeof document !== 'undefined' ? document.documentElement.lang : null
  const activeLocale =
    locale || (htmlLang && htmlLang !== 'default' ? htmlLang.replace('_', '-') : 'fr-BE')
  return new Intl.NumberFormat(activeLocale, { maximumFractionDigits: 2 }).format(value)
}

// Builds the group header text. When a single column carries numeric data it stays
// "<label> — <total>" (unchanged from before). When several columns each carry their
// own numeric total (Base + Compare, or several series) it becomes
// "<label> — <col1 title>: <total1> | <col2 title>: <total2>" so the header never
// silently adds a base period and its compare period together.
export const buildGroupTotalLabel = (
  groupLabel,
  columnTotals,
  formatNumberFn = formatGroupNumber
) => {
  const withValues = (columnTotals ?? []).filter((c) => c.hasNumericValue)
  if (withValues.length === 0) return groupLabel ?? ''
  if (withValues.length === 1) {
    return `${groupLabel ?? ''} — ${formatNumberFn(withValues[0].total)}`
  }
  return `${groupLabel ?? ''} — ${withValues
    .map((c) => `${c.title}: ${formatNumberFn(c.total)}`)
    .join(' | ')}`
}

export const detailedTablePlugin = {
  id: 'detailedTablePlugin',
  afterUpdate(chart, args, options) {
    const container = document.getElementById(options.containerID)
    if (!container) return

    const frag = document.createDocumentFragment()

    // Helpers
    const el = (tag, classes = [], props = {}) => {
      const node = document.createElement(tag)
      if (classes?.length) node.classList.add(...classes)
      Object.assign(node, props)
      return node
    }
    const td = (text = '') => {
      const n = el('td')
      // Nullish/empty fallback uniquement : un `0` num\u00E9rique l\u00E9gitime doit
      // s'afficher tel quel, pas \u00EAtre trait\u00E9 comme "pas de donn\u00E9e"
      n.textContent = text === null || text === undefined || text === '' ? '\u00A0' : text
      return n
    }
    const pick = (v) => (Array.isArray(v) ? v[0] : v)

    // 1) MODE: 'paired' (Base/Compare) ou 'series' (une colonne par dataset)
    const mode =
      options?.mode ?? (chart.config.type === 'bar' ? 'paired' : 'series')
    const orderedStacks = options?.stacks ?? ['base', 'compare'] // utilisé seulement en 'paired'
    const keepPlaceholderForMissingStack = options?.keepPlaceholder ?? true // garde colonne vide si stack absente
    const router = options?.router

    // Lien routeur réutilisable (cellules principales + cellules meta)
    const makeRouterLink = (
      routerInstance,
      text,
      to,
      className = 'text-blue-600 hover:underline'
    ) => {
      const a = document.createElement('a')
      a.textContent = text
      a.className = className
      if (routerInstance && typeof routerInstance.resolve === 'function') {
        const resolved = routerInstance.resolve(to)
        a.href = resolved.href
        a.addEventListener('click', (e) => {
          if (
            e.metaKey ||
            e.ctrlKey ||
            e.shiftKey ||
            e.altKey ||
            e.button !== 0 ||
            a.target === '_blank'
          )
            return
          e.preventDefault()
          routerInstance.push(to)
        })
      } else {
        a.href = typeof to === 'string' ? to : (to?.path ?? '#')
      }
      return a
    }

    // Cellule <td> générique : lien routeur si `route` présent, sinon texte brut
    const makeCellTd = (text, route) => {
      if (route) {
        const tdLink = document.createElement('td')
        tdLink.appendChild(makeRouterLink(router, text, route))
        return tdLink
      }
      return td(text)
    }

    const table = el('table', [], {
      id: 'statistic-table'
    })

    const datasets = chart.data.datasets || []
    const grouped =
      Object.groupBy?.(datasets, (d) => d.label) ??
      datasets.reduce((acc, d) => ((acc[d.label] ||= []).push(d), acc), {})
    const labels = chart.data.labels || []
    const isVisibleIdx = (idx) =>
      chart.isDatasetVisible ? chart.isDatasetVisible(idx) !== false : true

    // Map dataset ref -> index
    const dsIndexByRef = new Map()
    datasets.forEach((ds, i) => dsIndexByRef.set(ds, i))

    // Couleur du groupe (bar: backgroundColor / line: borderColor si dispo)
    const groupColorOf = (firstIdx) =>
      pick(datasets[firstIdx]?.borderColor) ||
      pick(datasets[firstIdx]?.backgroundColor) ||
      '#999'

    // Construit le plan de colonnes selon le mode
    const buildColumns = (groupDatasets) => {
      if (mode === 'paired') {
        const dsByStack = Object.fromEntries(
          orderedStacks.map((stk) => [
            stk,
            groupDatasets.find((d) => d.stack === stk) || null
          ])
        )

        const cols = orderedStacks
          .map((stk) => {
            const ds = dsByStack[stk]
            if (!ds) {
              return keepPlaceholderForMissingStack
                ? {
                    key: `stk:${stk}`,
                    title: stk === 'base' ? 'Base' : 'Compare',
                    details: [], // colonne vide
                    visible: true // on garde la colonne pour ne pas décaler
                  }
                : null
            }
            const idx = dsIndexByRef.get(ds)
            return {
              key: `stk:${stk}`,
              title: stk === 'base' ? 'Base' : 'Compare',
              details: ds.details ?? [],
              visible: isVisibleIdx(idx)
            }
          })
          .filter(Boolean)

        // Si toutes colonnes 'paired' sont invisibles, on rend rien
        if (cols.every((c) => !c.visible)) return []

        // Ne pas afficher les colonnes invisibles
        return cols.filter(
          (c) =>
            c.visible ||
            (c.details.length === 0 && keepPlaceholderForMissingStack)
        )
      }

      // mode === 'series' : une colonne par dataset du groupe
      const cols = groupDatasets
        .map((ds, localIdx) => {
          const idx = dsIndexByRef.get(ds)
          return {
            key: `ds:${idx}`,
            title:
              ds.stack ?? ds.variant ?? ds.subLabel ?? `Série ${localIdx + 1}`,
            details: ds.details ?? [],
            visible: isVisibleIdx(idx)
          }
        })
        .filter((c) => c.visible) // en 'series', on retire ce qui est masqué

      return cols
    }

    // rendu par groupe
    Object.entries(grouped).forEach(([groupLabel, groupDatasets]) => {
      const firstIdx = dsIndexByRef.get(groupDatasets[0]) ?? 0
      const groupColor = groupColorOf(firstIdx)

      const columns = buildColumns(groupDatasets)
      if (!columns || columns.length === 0) return

      const section = el('tbody')

      // Total(aux) du groupe : un total PAR colonne (Base/Compare, ou par série), jamais
      // fusionné entre colonnes — voir computeColumnTotals ci-dessus (BBSW-9807).
      const columnTotals = computeColumnTotals(columns)

      const metaLabels = []
      columns.forEach((col) => {
        ;(col.details ?? [])
          .flat()
          .filter(Boolean)
          .forEach((d) => {
            ;(d.meta ?? []).forEach((m) => {
              if (!metaLabels.includes(m.label)) metaLabels.push(m.label)
            })
          })
      })

      // Titre de groupe + puce couleur
      const groupTr = el('tr', [])
      const groupTh = el('th', [], {
        colSpan: 1 + columns.length * (1 + metaLabels.length),
        scope: 'rowgroup'
      })
      const titleWrap = el('span')
      const dot = el('span')
      dot.style.cssText = `display:inline-block;width:10px;height:10px;border-radius:9999px;margin-right:6px;background:${groupColor}`
      const title = el('span')
      // N'affiche un total que si au moins une `value` numérique existe dans ce groupe
      // (évite un total factice "— 0" sur les vues non migrées) ; le total est calculé
      // par colonne, jamais fusionné entre Base et Compare (buildGroupTotalLabel).
      title.textContent = buildGroupTotalLabel(groupLabel, columnTotals)
      titleWrap.append(dot, title)
      groupTh.append(titleWrap)
      groupTr.appendChild(groupTh)
      section.appendChild(groupTr)

      // Ligne d'intitulés (Label + [titre de colonne + titres meta] par colonne)
      const headerRow = el('tr', [])

      headerRow.appendChild(td('Label'))
      columns.forEach((col) => {
        headerRow.appendChild(td(col.title))
        metaLabels.forEach((label) => headerRow.appendChild(td(label)))
      })
      section.appendChild(headerRow)

      // Données
      for (let j = 0; j < labels.length; j++) {
        // Nombre de sous-lignes max pour ce label
        const lengths = columns.map((col) => col.details?.[j]?.length || 0)
        const max = Math.max(1, ...lengths)

        for (let k = 0; k < max; k++) {
          const row = document.createElement('tr')

          // 1) label répété à chaque sous-ligne (facile pour CSV/Excel)
          const thLabel = el('th', [], { scope: 'row', vAlign: 'top' })
          thLabel.textContent = k === 0 ? (labels[j] ?? '') : ''
          row.appendChild(thLabel)

          // 2) colonnes (cellule principale + une cellule par libellé meta)
          columns.forEach((col) => {
            const rowArr = col.details?.[j] ?? []
            const cellData = rowArr[k]

            row.appendChild(makeCellTd(cellData?.title, cellData?.route))

            metaLabels.forEach((label) => {
              const metaEntry = cellData?.meta?.find((m) => m.label === label)
              const metaValue = metaEntry?.value ?? ''
              row.appendChild(makeCellTd(metaValue, metaEntry?.route))
            })
          })

          section.appendChild(row)
        }
      }

      table.appendChild(section)
    })

    frag.appendChild(table)
    container.replaceChildren(frag)
  }
}
